import {
  BILLABLE_LEVELS,
  LEGACY_COUNTRY_ALIASES,
  WORLD_BANK_TIERS,
  countryEndonyms,
  normalizeCountryKey,
} from '../locales/countries.js';
import { DEFAULT_MULTIPLIERS } from '../locales/economicData.js';

export const REGISTRATION_MODES = {
  ONLINE: 'online',
  PARTNER: 'partner',
  CLOSED: 'closed',
};

export const MODE_LABELS = {
  online: 'Online (Stripe)',
  partner: 'Partner code',
  closed: 'Not offered',
};

/** Default registration mode when no explicit country rule exists (schema v2+). */
export const DEFAULT_MODE_BY_LEVEL = {
  foundation: REGISTRATION_MODES.ONLINE,
  certified: REGISTRATION_MODES.CLOSED,
  accredited: REGISTRATION_MODES.CLOSED,
};

export function isRegistrationsPaused(config) {
  return config?.registrationsPaused === true || config?.globalMarketsDisabled === true;
}

function legacyDisabled(config, country, level) {
  const c = normalizeCountryKey(country);
  return (
    config?.disabledMarkets?.[c]?.[level] === true ||
    config?.disabledMarkets?.[country]?.[level] === true
  );
}

function explicitRule(config, country, level) {
  const c = normalizeCountryKey(country);
  return config?.countryRules?.[c]?.[level] || config?.countryRules?.[country]?.[level] || null;
}

/**
 * Resolve registration mode for a country × level.
 * Stored countryRules (schema v2+) override defaults.
 * Legacy disabledMarkets still force closed.
 * Otherwise: Foundation online; Certified / Global not offered.
 */
export function resolveRegistrationMode(country, level, config, options = {}) {
  if (!options.ignoreGlobalPause && isRegistrationsPaused(config)) return REGISTRATION_MODES.CLOSED;

  const explicit = explicitRule(config, country, level);
  if (explicit?.mode && Object.values(REGISTRATION_MODES).includes(explicit.mode)) {
    return explicit.mode;
  }

  if (legacyDisabled(config, country, level)) return REGISTRATION_MODES.CLOSED;

  return DEFAULT_MODE_BY_LEVEL[level] || REGISTRATION_MODES.CLOSED;
}

export function resolveCountryLevelRule(country, level, config, options = {}) {
  const c = normalizeCountryKey(country);
  const explicit = explicitRule(config, country, level);
  const mode = resolveRegistrationMode(c, level, config, options);
  return {
    country: c,
    level,
    mode,
    available: mode !== REGISTRATION_MODES.CLOSED,
    requiresPartnerCode: mode === REGISTRATION_MODES.PARTNER,
    requiresStripe: mode === REGISTRATION_MODES.ONLINE,
    partnerName: explicit?.partnerName || '',
    partnerId: explicit?.partnerId || '',
    feeGbpOverride:
      explicit?.feeGbp != null && Number.isFinite(Number(explicit.feeGbp))
        ? Math.round(Number(explicit.feeGbp))
        : null,
  };
}

export function computeFeeGbp(country, level, config) {
  const c = normalizeCountryKey(country);
  const rule = resolveCountryLevelRule(c, level, config);
  if (rule.feeGbpOverride != null) return rule.feeGbpOverride;

  if (config?.countryOverrides?.[c]?.[level] != null) {
    return Math.round(Number(config.countryOverrides[c][level]));
  }
  if (config?.countryOverrides?.[country]?.[level] != null) {
    return Math.round(Number(config.countryOverrides[country][level]));
  }

  const tier = WORLD_BANK_TIERS[c] || 1;
  if (config?.tierOverrides?.[tier]?.[level] != null) {
    return Math.round(Number(config.tierOverrides[tier][level]));
  }

  const base = Number(config?.baseRates?.[level]) || 0;
  const multiplier = DEFAULT_MULTIPLIERS[tier] || 1;
  return Math.round(base * multiplier);
}

export function getCalculatedFee(country, level, config) {
  const c = normalizeCountryKey(country);
  const rule = resolveCountryLevelRule(c, level, config);
  const amount = computeFeeGbp(c, level, config);
  return {
    amount,
    available: rule.available,
    mode: rule.mode,
    requiresPartnerCode: rule.requiresPartnerCode,
    requiresStripe: rule.requiresStripe,
    partnerName: rule.partnerName,
  };
}

/**
 * Renewal / upgrade uses the same country × level matrix, but a pause on
 * brand-new registrations does not close an existing school's next cycle.
 */
export function getRenewalMarketQuote(country, level, config) {
  const c = normalizeCountryKey(country);
  const rule = resolveCountryLevelRule(c, level, config, { ignoreGlobalPause: true });
  const amount = computeFeeGbp(c, level, config);
  return {
    amount,
    available: rule.available,
    mode: rule.mode,
    requiresPartnerCode: rule.requiresPartnerCode,
    requiresStripe: rule.requiresStripe,
    partnerName: rule.partnerName,
  };
}

/** Levels open for registration in this country (excludes closed). */
export function getOpenLevelsForCountry(country, config) {
  return BILLABLE_LEVELS.filter((level) => {
    return resolveRegistrationMode(country, level, config) !== REGISTRATION_MODES.CLOSED;
  });
}

/** Alternative levels when the selected level is closed. */
export function getAlternativeLevels(country, selectedLevel, config) {
  return getOpenLevelsForCountry(country, config).filter((l) => l !== selectedLevel);
}

export function levelDisplayLabel(level) {
  if (level === 'accredited') return 'Global';
  if (level === 'certified') return 'Certified';
  if (level === 'foundation') return 'Foundation';
  return level;
}

/**
 * Build editable countryRules for admin UI from stored config (legacy + v2).
 */
export function hydrateCountryRulesForAdmin(config) {
  if (!config) return {};
  const stored =
    Number(config?.schemaVersion) >= 2 &&
    config.countryRules &&
    Object.keys(config.countryRules).length > 0
      ? config.countryRules
      : {};
  const rules = {};

  for (const country of countryEndonyms) {
    rules[country] = {};
    for (const level of BILLABLE_LEVELS) {
      const explicit = stored[country]?.[level];
      const mode =
        explicit?.mode ||
        resolveRegistrationMode(country, level, { ...config, countryRules: stored });
      let feeGbp = null;
      if (explicit?.feeGbp != null && Number.isFinite(Number(explicit.feeGbp))) {
        feeGbp = Math.round(Number(explicit.feeGbp));
      } else if (config.countryOverrides?.[country]?.[level] != null) {
        feeGbp = Math.round(Number(config.countryOverrides[country][level]));
      }
      rules[country][level] = {
        mode,
        feeGbp,
        partnerName: explicit?.partnerName || '',
        partnerId: explicit?.partnerId || '',
      };
    }
  }
  return rules;
}

export function summarizeCountryRow(country, config, countryRules) {
  const modes = BILLABLE_LEVELS.map(
    (l) => (countryRules?.[country]?.[l]?.mode || resolveRegistrationMode(country, l, config))
  );
  const allOnline = modes.every((m) => m === REGISTRATION_MODES.ONLINE);
  const allClosed = modes.every((m) => m === REGISTRATION_MODES.CLOSED);
  const hasPartner = modes.some((m) => m === REGISTRATION_MODES.PARTNER);
  if (allClosed) return 'All closed';
  if (allOnline) return 'All online';
  if (hasPartner) return 'Partner mix';
  const open = modes.filter((m) => m !== REGISTRATION_MODES.CLOSED).length;
  return `${open} level(s) open`;
}

/** Persistable countryRules map (explicit modes only). */
export function sanitizeCountryRulesForFirestore(raw) {
  const src = raw || {};
  const out = {};
  for (const [country, levels] of Object.entries(src)) {
    if (!levels || typeof levels !== 'object') continue;
    const row = {};
    for (const lvl of BILLABLE_LEVELS) {
      const cell = levels[lvl];
      if (!cell || typeof cell !== 'object') continue;
      const mode = cell.mode;
      if (!Object.values(REGISTRATION_MODES).includes(mode)) continue;
      const entry = { mode };
      if (cell.feeGbp != null && Number.isFinite(Number(cell.feeGbp))) {
        entry.feeGbp = Math.round(Number(cell.feeGbp));
      }
      if (typeof cell.partnerName === 'string' && cell.partnerName.trim()) {
        entry.partnerName = cell.partnerName.trim();
      }
      if (typeof cell.partnerId === 'string' && cell.partnerId.trim()) {
        entry.partnerId = cell.partnerId.trim();
      }
      row[lvl] = entry;
    }
    if (Object.keys(row).length) out[country] = row;
  }
  return out;
}

/** Legacy corporate-hub field: true where mode is closed (derived on publish). */
export function deriveDisabledMarketsFromCountryRules(countryRules) {
  const out = {};
  for (const [country, levels] of Object.entries(countryRules || {})) {
    if (!levels || typeof levels !== 'object') continue;
    for (const lvl of BILLABLE_LEVELS) {
      if (levels[lvl]?.mode === REGISTRATION_MODES.CLOSED) {
        if (!out[country]) out[country] = {};
        out[country][lvl] = true;
      }
    }
  }
  return out;
}

function cloneJsonSafeMaps(obj) {
  try {
    return JSON.parse(JSON.stringify(obj == null ? {} : obj));
  } catch {
    return {};
  }
}

/**
 * Full pricing + availability payload for settings/pricing and public/fees.
 * Hydrates all countries so published docs are self-contained for ifip.group.
 */
export function buildPublishedPricingPayload(config) {
  const registrationsPaused =
    config?.registrationsPaused === true || config?.globalMarketsDisabled === true;
  const br = config?.baseRates || {};
  const countryRules = sanitizeCountryRulesForFirestore(
    hydrateCountryRulesForAdmin({ ...config, schemaVersion: 2 })
  );

  return {
    schemaVersion: 2,
    baseRates: {
      foundation: Math.round(Number(br.foundation ?? 0)),
      certified: Math.round(Number(br.certified ?? 0)),
      accredited: Math.round(Number(br.accredited ?? 0)),
    },
    tierOverrides: cloneJsonSafeMaps(config?.tierOverrides),
    countryOverrides: cloneJsonSafeMaps(config?.countryOverrides),
    countryRules,
    disabledMarkets: deriveDisabledMarketsFromCountryRules(countryRules),
    registrationsPaused,
    globalMarketsDisabled: registrationsPaused,
  };
}

export { BILLABLE_LEVELS, countryEndonyms, normalizeCountryKey, LEGACY_COUNTRY_ALIASES };
